
import { nextTick, onMounted, ref, watch, defineComponent } from 'vue';
import { v4 as uuidv4 } from 'uuid';

/**
 * This component wraps the componentToLoad slot with a skeleton loader and attempts to accurately fit down to the component's bounding rect.
 *
 * To shadow a dynamic element you can declare a dummy element before the shadowed element that's conditional based on the loading prop. e.g.
 *
 * <SkeletonLoader :loading="loading">
 *   <template #componentToLoad>
 *     <Button v-if="loading" label="'dummyData'" />
 *     <Button v-else-if="buttonData" label="buttonData" />
 *   </template>
 * </SkeletonLoader>
 *
 * (here a skeleton loader would appear to wrap a potential button that may or may not appear after loading and resolving buttonData)
 *
 * Issues: the span wrapping componentToLoad won't fit-content accurately when componentToLoad uses a width percentage
 */
export default defineComponent({
  name: 'SkeletonLoader',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    marginAuto: {
      type: Boolean,
      default: true,
    },
  },
  setup(props: any) {
    const id = ref(`component-to-load-${uuidv4()}`);
    const myWidth = ref(300);
    const myHeight = ref(20);

    const resizeRect = () => {
      if (typeof document !== 'undefined') {
        const rect = document.getElementById(id.value)?.getBoundingClientRect();
        myWidth.value = rect != null ? rect.width : myWidth.value;
        myHeight.value = rect != null ? rect.height : myHeight.value;
      }
    };

    onMounted(() => {
      resizeRect();
    });

    watch(
      () => props.loading,
      (newVal, prevVal) => {
        if (newVal && !prevVal) {
          nextTick().then(() => {
            resizeRect();
          });
        }
      }
    );

    return {
      id,
      myWidth,
      myHeight,
    };
  },
});
